import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { IoCheckmark, IoPersonCircleOutline } from 'react-icons/io5'
import heroBackdrop from '../../assets/pixe.png'
import { useSessionFlags, markOnboardingComplete } from '../../state/session'
import { useMe } from '../../hooks/useMe'


export default function Ready() {
  const navigate = useNavigate()
  const { isAuthenticated } = useSessionFlags()
  const [params] = useSearchParams()
  const { me } = useMe(isAuthenticated)
  const providedHandle = params.get('handle') || ''
  const displayHandle = providedHandle || me?.username || me?.profile?.handle || ''

  useEffect(() => {
    if (!isAuthenticated) navigate('/login', { replace: true })
  }, [isAuthenticated, navigate])

  const enterApp = () => {
    markOnboardingComplete()
    navigate('/', { replace: true })
  }

  const completeProfile = () => {
    markOnboardingComplete()
    navigate('/people/edit')
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-[#050505] via-[#08090f] to-black text-white">
      <div className="pointer-events-none absolute inset-0 opacity-70">
        <img src={heroBackdrop} alt="" className="h-full w-full object-cover blur-[140px]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,0,122,0.35),_transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,_rgba(0,122,255,0.2),_transparent_55%)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/75" />
      </div>

      <div className="relative mx-auto w-full max-w-5xl px-4 pb-20 pt-16 sm:px-6">
        <div className="mb-10 flex items-center gap-3">
          <span className="text-xs font-semibold uppercase tracking-[0.4em] text-white/60">Handle secured</span>
        </div>

        <div className="grid gap-12 text-center lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:text-left">
          <section className="space-y-6">
            <div className="space-y-3">
              <h1 className="text-3xl font-black md:text-4xl">Handle secured</h1>
              <p className="text-base text-white/75 md:text-lg">
                @{displayHandle || 'yourhandle'} is officially yours. Finish your profile, drop a first post, or jump
                straight into the feed—everything syncs with the mobile app.
              </p>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/5 p-5 text-left text-sm text-white/80 shadow-[0_25px_70px_rgba(0,0,0,0.4)] backdrop-blur">
              <p className="font-semibold uppercase tracking-[0.3em] text-white/50">Next up</p>
              <ul className="mt-3 list-disc space-y-1 pl-5">
                <li>Add a bio, avatar, and links so followers can find you.</li>
                <li>Explore the reader and store to see what’s trending.</li>
                <li>Invite friends to follow your new handle.</li>
              </ul>
            </div>
            <p className="text-sm text-white/65">You can edit all of this anytime from Settings → Profile.</p>
          </section>

          <section className="flex flex-col items-center rounded-[32px] border border-white/10 bg-white/5 p-8 text-center shadow-[0_40px_110px_rgba(0,0,0,0.55)]">
            <div className="flex h-28 w-28 items-center justify-center rounded-full bg-[#1d4ed8] shadow-[0_25px_60px_rgba(0,0,0,0.45)]">
              <div className="flex h-24 w-24 items-center justify-center rounded-full bg-black/70">
                <IoCheckmark className="h-10 w-10 text-white" />
              </div>
            </div>

            <h2 className="mt-8 text-3xl font-black">
              {displayHandle ? `@${displayHandle}` : 'Handle claimed!'}
            </h2>
            <p className="mt-2 text-base text-white/70">Minting on-chain in the background.</p>

            <div className="mt-8 w-full space-y-4">
              <GradientButton onClick={enterApp}>Enter PixeLoop</GradientButton>
              <OutlineButton onClick={completeProfile} icon={<IoPersonCircleOutline className="h-5 w-5" />}>
                Complete profile
              </OutlineButton>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}

function GradientButton({
  children,
  onClick,
}: {
  children: ReactNode
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded-2xl bg-[#1d4ed8] px-6 py-3 text-base font-bold text-white shadow-[0_15px_35px_rgba(0,0,0,0.45)] transition hover:opacity-95"
    >
      {children}
    </button>
  )
}

function OutlineButton({
  children,
  onClick,
  icon,
}: {
  children: ReactNode
  onClick: () => void
  icon?: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-center gap-2 rounded-2xl border border-white/15 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/5"
    >
      {icon}
      {children}
    </button>
  )
}
